import RevealOnScroll from '../base/RevealOnScroll';
import { services } from '../../mocks/services';

interface Props {
  title?: string;
  subtitle?: string;
  layout?: 'cards' | 'detailed';
  bg?: 'cream' | 'white' | 'dark';
}

export default function ServicesSection({
  title = 'Professional Property Solutions',
  subtitle = 'Six core services. One trusted provider. Delivered with precision, care, and a 100% satisfaction guarantee across Houston TX.',
  layout = 'cards',
  bg = 'white',
}: Props) {
  const bgClass = bg === 'dark' ? 'bg-[#0F0F0F]' : bg === 'cream' ? 'bg-[#F8F4EF]' : 'bg-white';
  const headingColor = bg === 'dark' ? 'text-white' : 'text-[#0F0F0F]';
  const subColor = bg === 'dark' ? 'text-white/45' : 'text-[#7A7265]';

  return (
    <section id="services" className={`${bgClass} py-28 lg:py-36`}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        <RevealOnScroll className="mb-18">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-px bg-[#BF9448]" />
            <span className="text-[#BF9448] text-[10px] font-sans font-semibold tracking-[0.25em] uppercase">
              Our Services
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end gap-6 lg:gap-16">
            <h2
              className={`font-serif font-semibold ${headingColor} leading-tight`}
              style={{ fontSize: 'clamp(2rem, 3.5vw, 3rem)' }}
            >
              {title}
            </h2>
            <p className={`font-sans ${subColor} text-base leading-relaxed max-w-lg lg:pb-1`}>
              {subtitle}
            </p>
          </div>
        </RevealOnScroll>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E8E2D9]">
          {services.map((service, i) => (
            <RevealOnScroll key={service.id} delayMs={i * 60}>
              <div className="group bg-white hover:bg-[#FDFAF7] transition-colors duration-400 overflow-hidden cursor-default">
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-700 ease-out"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  {/* Service name overlay at bottom of image */}
                  <div className="absolute bottom-0 left-0 right-0 px-6 pb-4 pt-12">
                    <div className="flex items-center gap-2.5">
                      <span className="w-6 h-6 flex items-center justify-center">
                        <i className={`${service.icon} text-[#BF9448] text-base`}></i>
                      </span>
                      <h3 className="font-serif text-white text-lg font-semibold leading-tight text-shadow-sm">
                        {service.name}
                      </h3>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6 pb-7">
                  <p className="font-sans text-[#6B6358] text-sm leading-relaxed mb-5">
                    {service.shortDesc}
                  </p>

                  {layout === 'detailed' && (
                    <ul className="space-y-2 mb-6">
                      {service.details.slice(0, 5).map((d, di) => (
                        <li key={di} className="flex items-start gap-2.5 text-sm font-sans text-[#4A4440]">
                          <span className="w-4 h-4 flex items-center justify-center mt-0.5 flex-shrink-0">
                            <i className="ri-checkbox-circle-fill text-[#BF9448] text-xs"></i>
                          </span>
                          {d}
                        </li>
                      ))}
                    </ul>
                  )}

                  <a
                    href="#quote"
                    className="inline-flex items-center gap-2 text-[#BF9448] text-xs font-sans font-semibold tracking-wider uppercase hover:gap-3 transition-all duration-200 cursor-pointer"
                  >
                    Request Quote
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-arrow-right-line text-sm"></i>
                    </span>
                  </a>
                </div>
              </div>
            </RevealOnScroll>
          ))}
        </div>

        <RevealOnScroll className="mt-14 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between border-t border-[#E8E2D9] pt-10">
          <p className={`font-sans ${subColor} text-sm`}>
            Not sure which service you need? We&rsquo;ll assess your project for free.
          </p>
          <a
            href="#quote"
            className="bg-[#0F0F0F] hover:bg-[#1A1A1A] text-white font-sans text-xs font-semibold px-8 py-4 rounded-sm transition-colors duration-200 whitespace-nowrap cursor-pointer tracking-[0.12em] uppercase"
          >
            Get Free Property Assessment
          </a>
        </RevealOnScroll>

      </div>
    </section>
  );
}
